import { Alert } from "@gotts.ai/ui";
import { useCallback, useRef } from "react";

import { useDeployment, useWallet } from "../../context";
import { useTokenBalances } from "../../hooks/useTokenBalances";

import { AddLiquidityForm } from "./AddLiquidityForm";
import { PositionsOverview } from "./PositionsOverview";
import { PositionSummaryCard } from "./PositionSummaryCard";
import { useLiquidityHistogram } from "./useLiquidityHistogram";
import { useV2Positions } from "./useV2Positions";
import { useV3Positions } from "./useV3Positions";
import { useV4Positions } from "./useV4Positions";

import type { Address } from "viem";

export default function LiquidityPage() {
  const { walletClient, address, mode } = useWallet();
  const { deployment } = useDeployment();
  const {
    balances,
    loading: balancesLoading,
    connectionError,
    refetch: refetchBalances,
  } = useTokenBalances();

  const {
    positions: v3Positions,
    loading: v3Loading,
    poolTicks,
    refetch: refetchV3,
  } = useV3Positions();

  const { buckets: liqBuckets, loading: liqLoading } =
    useLiquidityHistogram(poolTicks);

  const {
    positions: v2Positions,
    loading: v2Loading,
    refetch: refetchV2,
  } = useV2Positions();

  const {
    positions: v4Positions,
    loading: v4Loading,
    poolTicks: v4PoolTicks,
    refetch: refetchV4,
    registerPosition: registerV4Position,
  } = useV4Positions();

  const isConnected = mode !== "disconnected" && !!walletClient && !!address;
  const totalPositions =
    v2Positions.length + v3Positions.length + v4Positions.length;
  const tokens = (deployment?.tokens ?? {}) as unknown as Record<
    string,
    Address
  >;

  const summaryRef = useRef<HTMLDivElement>(null);

  const handleRefresh = useCallback(() => {
    void refetchV2();
    void refetchV3();
    void refetchV4();
    refetchBalances();
    setTimeout(
      () => summaryRef.current?.scrollIntoView({ behavior: "smooth" }),
      500,
    );
  }, [refetchV2, refetchV3, refetchV4, refetchBalances]);

  const hasPositions = totalPositions > 0;

  return (
    <div className="hide-pcb-labels">
      {connectionError && (
        <Alert variant="error" title="Anvil Unreachable" className="mb-4">
          Cannot connect to Anvil at 127.0.0.1:8545. Is it running?
        </Alert>
      )}

      {hasPositions ? (
        /* Two-column layout: form (fixed) + positions (flex) */
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[420px_1fr]">
          {/* Left column: form */}
          <div className="space-y-4">
            <AddLiquidityForm
              isConnected={isConnected}
              onSuccess={handleRefresh}
              balances={balances}
              balancesLoading={balancesLoading}
              refetchBalances={refetchBalances}
              poolTicks={poolTicks}
              onV4PositionAdded={registerV4Position}
            />
          </div>

          {/* Right column: summary + positions */}
          <div className="space-y-4">
            <div ref={summaryRef}>
              <PositionSummaryCard
                v2Positions={v2Positions}
                v3Positions={v3Positions}
                v4Positions={v4Positions}
                poolTicks={poolTicks}
                tokens={tokens}
              />
            </div>
            <PositionsOverview
              isConnected={isConnected}
              onRefresh={handleRefresh}
              v3Positions={v3Positions}
              v3Loading={v3Loading}
              poolTicks={poolTicks}
              liqBuckets={liqBuckets}
              liqLoading={liqLoading}
              refetchV3={refetchV3}
              refetchBalances={refetchBalances}
              v4Positions={v4Positions}
              v4Loading={v4Loading}
              v4PoolTicks={v4PoolTicks}
              refetchV4={refetchV4}
              v2Positions={v2Positions}
              v2Loading={v2Loading}
              refetchV2={refetchV2}
            />
          </div>
        </div>
      ) : (
        /* Hero state: centered form + empty state */
        <div>
          <div className="mx-auto max-w-[420px]">
            <AddLiquidityForm
              isConnected={isConnected}
              onSuccess={handleRefresh}
              balances={balances}
              balancesLoading={balancesLoading}
              refetchBalances={refetchBalances}
              poolTicks={poolTicks}
              onV4PositionAdded={registerV4Position}
            />
          </div>
          <div ref={summaryRef} className="py-6 text-center">
            <p className="text-xs text-[var(--color-text-muted)]">
              No active positions — add liquidity to get started
            </p>
          </div>
          <PositionsOverview
            isConnected={isConnected}
            onRefresh={handleRefresh}
            v3Positions={v3Positions}
            v3Loading={v3Loading}
            poolTicks={poolTicks}
            liqBuckets={liqBuckets}
            liqLoading={liqLoading}
            refetchV3={refetchV3}
            refetchBalances={refetchBalances}
            v4Positions={v4Positions}
            v4Loading={v4Loading}
            v4PoolTicks={v4PoolTicks}
            refetchV4={refetchV4}
            v2Positions={v2Positions}
            v2Loading={v2Loading}
            refetchV2={refetchV2}
          />
        </div>
      )}
    </div>
  );
}
